import type { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  ANYWHERE_PLACE_ID,
} from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import useToggle from 'hooks/useToggle'
import { EmptyArray, take, without } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import type { ComponentProps } from 'react'
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import uuidV4 from 'lib/string/uuidV4Utils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import SearchPlaceSelectDropdown from './SearchPlaceSelectDropdown'
import SearchPlaceSelectInline from './SearchPlaceSelectInline'
import HotelSearchPlaceSelectDropdown from '../Hotels/HotelSearchPlaceSelectDropdown'
import TourSearchPlaceSelectDropdown from '../TourV2/TourSearchPlaceSelectDropdown'
import ExperiencesSearchPlaceSelectDropdown from '../Experiences/ExperiencesSearchPlaceSelectDropdown'
import ExperiencesSearchPlaceSelectInline from '../Experiences/ExperiencesSearchPlaceSelectInline'

const components: Record<'dropdown' | 'inline', Utils.PartialRecord<App.SearchPlaceVertical | 'default', any>> = {
  // desktop
  dropdown: {
    default: SearchPlaceSelectDropdown,
    hotel: HotelSearchPlaceSelectDropdown,
    tour: TourSearchPlaceSelectDropdown,
    experience: ExperiencesSearchPlaceSelectDropdown,
  },
  // mobile
  inline: {
    default: SearchPlaceSelectInline,
    experience: ExperiencesSearchPlaceSelectInline,
  },
}

interface MappedStateProps {
  pathname: string
}

interface Props {
  open?: boolean
  className?: string
  ['data-testid']?: string
  label?: string
  value?: App.SearchItem
  placeholder?: string
  searchTypes?: Array<App.SearchType>
  placeTypes?: Array<App.SearchPlaceType>
  searchVertical?: App.SearchPlaceVertical
  /** Items to show when a user hasn't entered any search */
  placeholderItems?: Array<App.SearchItem>
  placeholderTitle?: string
  /** When the selected item changes */
  onChange?: (item?: App.SearchItem) => void
  onToggle?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean) => void
  inputRef?: React.Ref<HTMLInputElement | null>
  controlsContainerRef?: React.RefObject<HTMLDivElement | null>
  required?: boolean
  /** A list of recent search items, will be displayed along with placeholder items */
  recentSearchItems?: Array<App.FullRecentSearch>
  /** Use this to supply your own search results list the input will use */
  overrideSearchItems?: Array<App.SearchItem>
  variant: 'inline' | 'dropdown'
  dropdownSize?: ComponentProps<typeof SearchPlaceSelectDropdown>['size']
  /**
   * Dismiss the dropdown once an item is selected
   *
   * @default true
   */
  closeOnChange?: boolean
  onClear?: () => void
  onPhraseChange?: (searchPhrase: string) => void
  multiline?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  /** The path to use for the search */
  searchPath?: string
  /** The path to use for the search map */
  searchMapPath?: string
}

const SELECTABLE_RESULT_TYPES: Array<App.SearchItemType> = [
  'destination',
  'property',
  'landmark',
  'airport',
  'departurePort',
  'lat_long',
  'searchItemV3',
]

/**
 * @todo NEEDS OVERHAUL
 */
function SearchPlaceSelect(props: Props & MappedStateProps) {
  const locationLabels = useLocationSearchLabels()
  const {
    open,
    placeholder = locationLabels.placeholderLabel,
    label = locationLabels.titleLabel,
    value,
    placeholderItems = EmptyArray,
    onChange = noop,
    onResultsChange = noop,
    inputRef,
    recentSearchItems = EmptyArray,
    closeOnChange = true,
    onToggle,
    pathname,
    variant,
    dropdownSize,
    controlsContainerRef,
    searchVertical,
    onClear = noop,
    onPhraseChange = noop,
    multiline = false,
    showNaturalLanguageSearchSuggestions = false,
    temporarySearchSubmitRef,
    searchPath,
    searchMapPath,
  }: Props & MappedStateProps = props

  const [localRecentSearches, setLocalRecentSearches] = useState<Array<App.FullRecentSearch>>(recentSearchItems)
  const [searchPhrase, setSearchPhrase] = useState<string>(value?.format.mainText ?? '')
  const [selectedDropdown, setSelectedDropdown] = useState<number | undefined>()
  // Tracks whether the current value was explicitly selected by the user (vs set from extraction/URL)
  // Using a ref to avoid re-renders that could cause side effects on mobile
  const hasUserSelectedValueRef = useRef<boolean>(false)
  const localInputRef = useRef<HTMLInputElement>(null)
  const [localOpen, , openDropdown, closeDropdown] = useToggle()
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const [resultsLoading, setResultsLoading] = useState<boolean>(false)
  const finalOpen = !!(open ?? localOpen)
  const dispatch = useAppDispatch()

  // Temporary: Generate stable typeaheadId at this level so it persists even when SearchPlaceSelectList unmounts
  const temporaryTypeaheadId = useMemo(() => {
    if (showNaturalLanguageSearchSuggestions) {
      return uuidV4()
    }
    return undefined
  }, [showNaturalLanguageSearchSuggestions])

  useEffect(() => {
    onPhraseChange(searchPhrase)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPhrase])

  // Temporary: Update ref with typeaheadId and searchPhrase for search submission
  // This ensures the ref is set even when SearchPlaceSelectList is not rendered
  useEffect(() => {
    if (temporarySearchSubmitRef && temporaryTypeaheadId && showNaturalLanguageSearchSuggestions && searchPhrase.length >= 2) {
      // Update properties in place to preserve cancelDebounce set by child component
      if (temporarySearchSubmitRef.current) {
        temporarySearchSubmitRef.current.typeaheadId = temporaryTypeaheadId
        temporarySearchSubmitRef.current.searchPhrase = searchPhrase
      } else {
        temporarySearchSubmitRef.current = {
          typeaheadId: temporaryTypeaheadId,
          searchPhrase,
        }
      }
    } else if (temporarySearchSubmitRef && (!showNaturalLanguageSearchSuggestions || !temporaryTypeaheadId)) {
      temporarySearchSubmitRef.current = null
    }
  }, [showNaturalLanguageSearchSuggestions, searchPhrase, temporarySearchSubmitRef, temporaryTypeaheadId])

  useEffect(() => {
    setLocalRecentSearches(recentSearchItems)
  }, [recentSearchItems])

  useEffect(() => {
    // Don't update from external value if user is actively editing (dropdown is open)
    if (finalOpen) {
      return
    }

    if (value?.format.mainText) {
      // update search phrase if search item updated
      setSearchPhrase(value.format.mainText)
      // Reset the user selection flag when value changes externally (e.g., from extraction)
      hasUserSelectedValueRef.current = false
    }
  }, [value, finalOpen])

  useEffect(() => {
    if (searchPhrase === '') {
      onClear()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPhrase])

  const handleOpen = useCallback((e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    openDropdown()
    if (onToggle) {
      onToggle(e)
    }
  }, [onToggle, openDropdown])

  useImperativeHandle(inputRef, () => localInputRef.current!)
  const onLocationSelect = useCallback((searchItem?: App.SearchItem) => {
    setSearchPhrase(searchItem?.format.mainText ?? '')
    // Mark that the user has explicitly selected this value (not from extraction)
    hasUserSelectedValueRef.current = !!searchItem
    onChange(searchItem)
    if (closeOnChange) {
      closeDropdown()
    }
  }, [onChange, closeOnChange, closeDropdown])

  const handleResultsChange = useCallback((results: Array<App.SearchItem>, searchPhrase: string, shouldTrack: boolean = true, loading: boolean = false) => {
    setResults(results)
    setResultsLoading(loading)
    onResultsChange(results, searchPhrase, shouldTrack)
  }, [onResultsChange])

  // Show placeholders when: (1) no search phrase and have placeholders, OR (2) user explicitly selected a value and searchPhrase matches it
  // hasUserSelectedValueRef ensures we don't show placeholders for extracted values (only for user-selected ones)
  const showPlaceholder = (searchPhrase.length === 0 && placeholderItems.length > 0) || !!(hasUserSelectedValueRef.current && value?.format.mainText === searchPhrase)
  const hasRecentSearchItems = !!localRecentSearches.length

  const [recentSearches, placeholdersSearches] = useMemo(() => {
    const filteredPlaceholderItems = placeholderItems.filter(item => item.value !== ANYWHERE_PLACE_ID)
    const uniqueSearchItems = take(localRecentSearches, 3)

    let maxItems = 7

    if (searchVertical === 'hotel') {
      maxItems = 15
    }

    return [
      uniqueSearchItems,
      take(filteredPlaceholderItems, maxItems - uniqueSearchItems.length),
    ]
  }, [localRecentSearches, placeholderItems, searchVertical])

  const onRecentSearchDelete = useCallback((item: App.FullRecentSearch) => {
    setLocalRecentSearches(without(localRecentSearches, item))
  }, [localRecentSearches])

  const onSearchPhraseChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.currentTarget.value)
    // clear the previous selected item during typing
    onChange(undefined)
  }, [onChange])

  const onClearClick = useCallback(() => {
    if (localInputRef.current) {
      localInputRef.current.value = ''
    }
    // clear the search phrase, selected item and suggested items
    setSearchPhrase('')
    onChange(undefined)
    onResultsChange([], '', false)
  }, [onChange, onResultsChange])

  const onRecentSearchClick = useCallback((recentSearch: App.FullRecentSearch) => {
    const { dates, rooms } = recentSearch

    const newSearch = encodeSearchParams({
      flexibleMonths: dates?.flexibleMonths,
      flexibleNights: dates?.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE,
      dates: {
        checkIn: dates.checkIn,
        checkOut: dates.checkOut,
      },
      urlSearch: '',
      rooms,
      searchItem: recentSearch.searchItem,
      userSelectedFlexibleMonths: !!dates?.flexibleMonths,
    }).toString()

    const pathToUse = pathname.includes('/map')
      ? (searchMapPath ?? '/search/map')
      : (searchPath ?? '/search')

    closeDropdown()
    dispatch(pushWithRegion(pathToUse, newSearch))
  }, [pathname, closeDropdown, dispatch, searchPath, searchMapPath])

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const selectableResults = results.filter(result => SELECTABLE_RESULT_TYPES.includes(result.searchType))
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (selectedDropdown === 0) {
        return
      }
      setSelectedDropdown(prev => prev ? prev - 1 : undefined)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (selectedDropdown === selectableResults.length - 1) {
        return
      }
      setSelectedDropdown(prev => prev === undefined ? 0 : prev + 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedDropdown === undefined) {
        if (!showPlaceholder && selectableResults.length > 0) {
          // Only select if loading is finished
          if (!resultsLoading) {
            onLocationSelect(selectableResults[0])
          }
        }
        return
      } else {
        if (showPlaceholder && selectedDropdown < recentSearches.length) {
          onRecentSearchClick(recentSearches[selectedDropdown])
        } else if (showPlaceholder && selectedDropdown > recentSearches.length - 1) {
          onLocationSelect(placeholdersSearches[selectedDropdown - recentSearches.length])
        } else if (!showPlaceholder) {
          if (selectedDropdown < selectableResults.length) {
            // Only select if loading is finished
            if (!resultsLoading) {
              onLocationSelect(selectableResults[selectedDropdown])
            }
          }
        }
      }
      setSelectedDropdown(undefined)
    } else {
      if (!finalOpen) {
        // if typing happens when the dropdown closed, open it
        handleOpen()
      }
    }
  }, [onLocationSelect, onRecentSearchClick, placeholdersSearches, recentSearches, results, selectedDropdown, showPlaceholder, finalOpen, handleOpen, resultsLoading])

  const SearchPlaceSelectProps = {
    ...props,
    onKeyDown,
    localInputRef,
    controlsContainerRef,
    searchPhrase,
    onSearchPhraseChange,
    handleOpen,
    onClearClick,
    finalOpen,
    closeDropdown,
    hasRecentSearchItems,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholdersSearches,
    onLocationSelect,
    showPlaceholder,
    handleResultsChange,
    label,
    placeholder,
    placeholderItems,
    setSearchPhrase,
    multiline,
    showNaturalLanguageSearchSuggestions,
    temporaryTypeaheadId,
  }

  const Component = components[variant][searchVertical ?? 'default'] ?? components[variant].default

  return <Component {...SearchPlaceSelectProps} {...(variant === 'dropdown' && { size: dropdownSize })} />
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    pathname: state.router.location.pathname,
  }
}

export default connect<MappedStateProps, undefined, Props, App.State>(mapStateToProps)(SearchPlaceSelect)
